import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
  Users, Calendar, Clock, TrendingUp, AlertTriangle, CheckCircle,
  XCircle, FileText, Building2, ClipboardList, RefreshCw,
  DollarSign, FileSignature, CalendarDays, UserCheck, Activity,
  Timer, ArrowUpRight, ArrowDownRight, Eye, Briefcase, Bell,
  BarChart3, PieChart, Coffee, UserMinus, Shield, Camera, Zap
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { todayStr, offsetDays } from '../utils/dateHelpers';
import { PendingApprovalsCard, MyPendingRequestsCard, useApprovalInbox } from '../components/common/ApprovalInboxCards';
import { GuardDutyAlertAdmin } from '../components/common/GuardDutyAlert';

const DASHBOARD_ROLES = ['ADMIN', 'DIRECTOR', 'MANAGER'];

const StatusBadge = ({ status }) => {
  const cfg = {
    present: { cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', label: 'Đúng giờ' },
    late: { cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', label: 'Đi muộn' },
    absent: { cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', label: 'Vắng' },
    early_leave: { cls: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300', label: 'Về sớm' },
    leave: { cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300', label: 'Nghỉ phép' },
    half_day: { cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300', label: 'Nửa ngày' },
  };
  const c = cfg[status] || cfg.present;
  return <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${c.cls}`}>{c.label}</span>;
};

const DonutChart = ({ segments, size = 140, strokeWidth = 14 }) => {
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const center = size / 2;
  let accOffset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={center} cy={center} r={r} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-gray-100 dark:text-gray-700" />
      {segments.map((seg, i) => {
        const dashLen = (seg.pct / 100) * c;
        const rotation = (accOffset / c) * 360 - 90;
        accOffset += dashLen;
        return (
          <circle key={i} cx={center} cy={center} r={r} fill="none"
            stroke={seg.color} strokeWidth={strokeWidth} strokeLinecap="round"
            strokeDasharray={`${dashLen} ${c - dashLen}`}
            transform={`rotate(${rotation} ${center} ${center})`}
            className="transition-all duration-700" />
        );
      })}
    </svg>
  );
};

const ProgressBar = ({ value, max, color = 'bg-blue-500' }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
      <div className={`h-2 rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

// Tiny inline sparkline (SVG) — series là mảng số
const Sparkline = ({ data = [], color = '#6366f1', width = 80, height = 28, fill = true }) => {
  if (!data.length) return <div style={{ width, height }} />;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const span = max - min || 1;
  const stepX = width / Math.max(data.length - 1, 1);
  const points = data.map((v, i) => `${i * stepX},${height - ((v - min) / span) * (height - 4) - 2}`).join(' ');
  const area = `0,${height} ${points} ${width},${height}`;
  return (
    <svg width={width} height={height} className="block">
      {fill && <polyline points={area} fill={color} fillOpacity="0.12" stroke="none" />}
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={(data.length - 1) * stepX} cy={height - ((data[data.length - 1] - min) / span) * (height - 4) - 2} r="2.5" fill={color} />
    </svg>
  );
};

// KPI card với big number + sparkline + delta + click-through
const KpiCard = ({ icon: Icon, label, value, sublabel, accent = 'blue', delta, deltaLabel, spark, onClick }) => {
  const palette = {
    blue: { ring: 'from-blue-500/15 to-blue-500/0', bar: 'text-blue-600 dark:text-blue-400', dot: '#3b82f6' },
    green: { ring: 'from-emerald-500/15 to-emerald-500/0', bar: 'text-emerald-600 dark:text-emerald-400', dot: '#10b981' },
    yellow: { ring: 'from-amber-500/15 to-amber-500/0', bar: 'text-amber-600 dark:text-amber-400', dot: '#f59e0b' },
    red: { ring: 'from-rose-500/15 to-rose-500/0', bar: 'text-rose-600 dark:text-rose-400', dot: '#ef4444' },
    purple: { ring: 'from-violet-500/15 to-violet-500/0', bar: 'text-violet-600 dark:text-violet-400', dot: '#8b5cf6' },
    orange: { ring: 'from-orange-500/15 to-orange-500/0', bar: 'text-orange-600 dark:text-orange-400', dot: '#f97316' },
    teal: { ring: 'from-teal-500/15 to-teal-500/0', bar: 'text-teal-600 dark:text-teal-400', dot: '#14b8a6' },
    indigo: { ring: 'from-indigo-500/15 to-indigo-500/0', bar: 'text-indigo-600 dark:text-indigo-400', dot: '#6366f1' },
  };
  const p = palette[accent] || palette.blue;
  const Cmp = onClick ? 'button' : 'div';
  return (
    <Cmp
      onClick={onClick}
      className={`group relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 p-4 ${onClick ? 'text-left hover:-translate-y-0.5 hover:shadow-lg hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer' : ''} transition-all duration-300`}
    >
      <div className={`pointer-events-none absolute -top-8 -right-8 w-28 h-28 bg-gradient-to-br ${p.ring} rounded-full blur-xl`} />
      <div className="relative flex items-start justify-between">
        <div className={`w-9 h-9 rounded-xl bg-gray-50 dark:bg-gray-700/50 flex items-center justify-center ${p.bar}`}>
          <Icon className="w-4.5 h-4.5" strokeWidth={2.2} />
        </div>
        {delta !== undefined && delta !== null && (
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${delta > 0 ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : delta < 0 ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' : 'bg-gray-50 text-gray-500 dark:bg-gray-700/50'}`}>
            {delta > 0 ? '▲' : delta < 0 ? '▼' : '·'} {Math.abs(delta)}{deltaLabel || ''}
          </span>
        )}
      </div>
      <div className="relative mt-3 flex items-end justify-between gap-2">
        <div className="min-w-0">
          <p className="text-2xl font-bold text-gray-900 dark:text-white leading-none tracking-tight">{value}</p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 truncate">{label}</p>
          {sublabel && <p className="text-[10px] text-gray-400 mt-0.5 truncate">{sublabel}</p>}
        </div>
        {spark?.length > 1 && <Sparkline data={spark} color={p.dot} width={70} height={28} />}
      </div>
    </Cmp>
  );
};

export default function DashboardPage() {
  const { user, hasPageAccess } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({ total_employees: 0, present_today: 0, late_today: 0, absent_today: 0, pending_leaves: 0, on_leave: 0, early_leave: 0, anomalies: 0, not_checked_in: 0 });
  const [adminStats, setAdminStats] = useState(null);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [topViolators, setTopViolators] = useState([]);
  const [deptStats, setDeptStats] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [otStats, setOtStats] = useState({ pending: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());
  // NHẮC NHỞ DUYỆT — hàng đợi của CHÍNH người đang đăng nhập (mọi loại đơn, kèm
  // tuổi đơn). Trước đây người duyệt chỉ biết có đơn khi tự mở trang Phê duyệt.
  const { data: inbox, loading: inboxLoading, reload: reloadInbox } = useApprovalInbox();

  const roleCode = user?.role?.role_code;
  const isAdmin = roleCode === 'ADMIN';
  const isDirector = roleCode === 'DIRECTOR';
  const isManager = roleCode === 'MANAGER';

  const canViewEmployees = hasPageAccess('/employee-management') || isAdmin || isDirector;
  const canViewAttendance = hasPageAccess('/attendance') || isAdmin || isDirector || isManager;
  const canViewReports = hasPageAccess('/reports') || isAdmin || isDirector;
  const canViewLeave = hasPageAccess('/leave-requests') || hasPageAccess('/approval-process') || isAdmin || isDirector || isManager;
  const canViewSupervision = hasPageAccess('/supervision') || isAdmin || isDirector;

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (roleCode && !DASHBOARD_ROLES.includes(roleCode)) {
      navigate('/portal', { replace: true });
    }
  }, [roleCode, navigate]);

  useEffect(() => {
    if (DASHBOARD_ROLES.includes(roleCode)) loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      if (!loading) setRefreshing(true);
      setLoading(true);
      const today = todayStr();
      const promises = [];

      if (isAdmin || isDirector || isManager) {
        promises.push(api.get('/reports/company-summary', { params: { start_date: today, end_date: today } }).catch(() => ({ data: { data: { summary: {} } } })));
      } else {
        promises.push(api.get('/reports/department', { params: { start_date: today, end_date: today } }).catch(() => ({ data: { data: {} } })));
      }

      if (canViewAttendance) {
        promises.push(api.get('/attendance', { params: { attendance_date: today, limit: 10, sort_by: 'check_in_time', sort_order: 'DESC' } }).catch(() => ({ data: { data: [] } })));
      } else {
        promises.push(Promise.resolve(null));
      }

      if (canViewReports || isManager) {
        const d30 = offsetDays(-30);
        promises.push(api.get('/reports/violations', { params: { start_date: d30, end_date: today, violation_type: 'all' } }).catch(() => ({ data: { data: { employee_summary: [] } } })));
      } else {
        promises.push(Promise.resolve(null));
      }

      if (canViewLeave) {
        promises.push(api.get('/leave-requests', { params: { status: 'pending', limit: 5 } }).catch(() => ({ data: { data: [] } })));
      } else {
        promises.push(Promise.resolve(null));
      }

      if (isAdmin) {
        promises.push(api.get('/hr/dashboard').catch(() => ({ data: { data: null } })));
      } else {
        promises.push(Promise.resolve(null));
      }

      if (isAdmin) {
        promises.push(api.get('/hr/dashboard/attendance-trends', { params: { days: 7 } }).catch(() => ({ data: { data: [] } })));
      } else if (canViewAttendance) {
        const d7 = offsetDays(-7);
        promises.push(api.get('/reports/company-summary', { params: { start_date: d7, end_date: today } }).catch(() => ({ data: { data: { daily_stats: [] } } })));
      } else {
        promises.push(Promise.resolve(null));
      }

      if (isAdmin || isDirector) {
        promises.push(api.get('/overtime', { params: { status: 'pending', limit: 1 } }).catch(() => ({ data: { data: [] } })));
      } else {
        promises.push(Promise.resolve(null));
      }

      if (canViewSupervision) {
        promises.push(api.get('/supervision-dashboard/dashboard').catch(() => ({ data: { data: null } })));
      } else {
        promises.push(Promise.resolve(null));
      }

      const [summaryRes, attendanceRes, violationsRes, leavesRes, hrDashRes, trendRes, otRes, supervisionRes] = await Promise.all(promises);

      if (isAdmin || isDirector || isManager) {
        if (summaryRes?.data?.success && summaryRes.data.data?.summary) {
          const s = summaryRes.data.data.summary;
          // total_employees đã LOẠI người được miễn chấm công (backend), nên
          // "đúng giờ / tổng nhân viên" ở dưới mới là một phép chia có nghĩa.
          setStats(prev => ({ ...prev, total_employees: s.total_employees || 0, exempt_employees: s.exempt_employees || 0, present_today: s.present_count || 0, late_today: s.late_count || 0, absent_today: s.absent_count || 0 }));
          if (summaryRes.data.data.department_breakdown) setDeptStats(summaryRes.data.data.department_breakdown.slice(0, 10));
        }
      } else {
        const d = Array.isArray(summaryRes?.data?.data) ? summaryRes.data.data[0] : summaryRes?.data?.data;
        if (d) setStats(prev => ({ ...prev, total_employees: d.total_employees || 0, present_today: d.present_count || 0, late_today: d.late_count || 0, absent_today: d.absent_count || 0 }));
      }

      if (supervisionRes?.data?.success && supervisionRes.data.data) {
        const sv = supervisionRes.data.data;
        setStats(prev => ({ ...prev, on_leave: sv.today?.onLeave || 0, early_leave: sv.today?.earlyLeave || 0, not_checked_in: sv.notCheckedIn || 0, anomalies: sv.anomaliesToday || 0 }));
      }

      if (attendanceRes?.data?.success) {
        const records = Array.isArray(attendanceRes.data.data) ? attendanceRes.data.data : (attendanceRes.data.data?.records || []);
        setRecentAttendance(records);
      }

      if (violationsRes?.data?.success && violationsRes.data.data?.employee_summary) setTopViolators(violationsRes.data.data.employee_summary.slice(0, 5));

      if (leavesRes?.data?.success) {
        const pd = leavesRes.data.data;
        const arr = Array.isArray(pd) ? pd : (pd?.records || []);
        setPendingLeaves(arr.slice(0, 5));
        setStats(prev => ({ ...prev, pending_leaves: Array.isArray(pd) ? pd.length : (pd?.total || arr.length) }));
      }

      if (hrDashRes?.data?.success && hrDashRes.data.data) setAdminStats(hrDashRes.data.data);

      if (trendRes?.data?.success) {
        let raw = isAdmin ? (trendRes.data.data || []) : (trendRes.data.data?.daily_stats || []);
        const dayNames = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
        setTrendData(raw.slice(-7).map(d => ({
          label: dayNames[new Date(d.attendance_date || d.date).getDay()],
          // Ép số — API có thể trả chuỗi ("0" truthy làm fallback chọn nhầm).
          on_time: Number(d.present ?? d.on_time) || 0,
          late: Number(d.late) || 0,
          absent: Number(d.absent) || 0,
        })));
      }

      if (otRes?.data?.success) {
        const total = otRes.data.pagination?.total || (Array.isArray(otRes.data.data) ? otRes.data.data.length : 0);
        setOtStats({ pending: total });
      }
    } catch (error) {
      console.error('Load dashboard error:', error);
      toast.error('Lỗi tải dữ liệu dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const attendanceRate = useMemo(() => stats.total_employees > 0 ? ((stats.present_today + stats.late_today) / stats.total_employees * 100) : 0, [stats]);
  const notCheckedIn = useMemo(() => Math.max(0, stats.total_employees - stats.present_today - stats.late_today - (stats.on_leave || 0) - stats.absent_today), [stats]);

  if (roleCode && !DASHBOARD_ROLES.includes(roleCode)) return null;

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Đang tải dashboard...</p>
        </div>
      </div>
    );
  }

  const roleBadge = user?.role?.role_name || { ADMIN: 'Quản trị viên', GENERAL_DIRECTOR: 'Tổng giám đốc', DIRECTOR: 'Giám đốc', MANAGER: 'Quản lý' }[roleCode] || roleCode;
  const todayFormatted = new Date().toLocaleDateString('vi-VN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = currentTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const donutSegments = [];
  const total = stats.total_employees || 1;
  if (stats.present_today > 0) donutSegments.push({ pct: (stats.present_today / total) * 100, color: '#10b981' });
  if (stats.late_today > 0) donutSegments.push({ pct: (stats.late_today / total) * 100, color: '#f59e0b' });
  if ((stats.on_leave || 0) > 0) donutSegments.push({ pct: ((stats.on_leave || 0) / total) * 100, color: '#8b5cf6' });
  if ((stats.early_leave || 0) > 0) donutSegments.push({ pct: ((stats.early_leave || 0) / total) * 100, color: '#f97316' });
  if (stats.absent_today > 0) donutSegments.push({ pct: (stats.absent_today / total) * 100, color: '#ef4444' });

  // Sparkline data từ trend 7 ngày
  const presentSpark = trendData.map(d => (d.on_time || 0) + (d.late || 0));
  const lateSpark = trendData.map(d => d.late || 0);
  const absentSpark = trendData.map(d => d.absent || 0);

  // Tổng số phút muộn (30 ngày) — hữu ích cho admin/director
  const totalLateMinutes = topViolators.reduce((sum, v) => sum + (v.total_late_minutes || 0), 0);

  // Phân bố check-in theo giờ (06h-22h) — từ recentAttendance hôm nay
  const hourlyBuckets = Array(17).fill(0); // 6→22h
  recentAttendance.forEach(r => {
    if (r.check_in_time) {
      const h = new Date(r.check_in_time).getHours();
      if (h >= 6 && h <= 22) hourlyBuckets[h - 6]++;
    }
  });
  const peakHourIdx = hourlyBuckets.indexOf(Math.max(...hourlyBuckets));
  const peakHour = hourlyBuckets[peakHourIdx] > 0 ? `${(peakHourIdx + 6).toString().padStart(2, '0')}h` : '—';

  // Phòng ban có tỷ lệ thấp (<70%) — cảnh báo
  const atRiskDepts = deptStats.filter(d => {
    const t = d.total_employees || d.total || 0;
    if (t === 0) return false;
    const c = (d.present_count || d.present || 0) + (d.late_count || d.late || 0);
    return (c / t) < 0.7;
  });

  // Greeting động theo giờ
  const hr = currentTime.getHours();
  const greeting = hr < 12 ? 'Chào buổi sáng' : hr < 18 ? 'Chào buổi chiều' : 'Chào buổi tối';

  // Trạng thái ca làm việc gợi ý
  const shiftPhase = hr < 6 ? { label: 'Ngoài giờ', color: 'bg-gray-400' }
    : hr < 9 ? { label: 'Giờ vào ca', color: 'bg-emerald-500' }
    : hr < 12 ? { label: 'Đang làm việc', color: 'bg-blue-500' }
    : hr < 14 ? { label: 'Giờ nghỉ trưa', color: 'bg-amber-500' }
    : hr < 17 ? { label: 'Đang làm việc', color: 'bg-blue-500' }
    : hr < 19 ? { label: 'Giờ tan ca', color: 'bg-purple-500' }
    : { label: 'Ngoài giờ', color: 'bg-gray-400' };

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pb-8">
      {/* ===== HERO ===== */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 text-white shadow-xl shadow-indigo-500/20">
        {/* Grid pattern background */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="absolute -top-16 -right-16 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 left-1/4 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl" />

        <div className="relative p-6 sm:p-7">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-white/70">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${shiftPhase.color} animate-pulse`} />
                {shiftPhase.label}
                <span className="text-white/30">•</span>
                <span className="capitalize">{todayFormatted}</span>
              </div>
              <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">
                {greeting}, <span className="text-white">{user?.full_name?.split(' ').slice(-1)[0] || ''}</span>
              </h1>
              <p className="text-white/70 text-sm mt-1">
                {isAdmin
                  ? 'Bạn đang quản lý toàn bộ hệ thống nhân sự.'
                  : isDirector
                  ? 'Tổng quan vận hành nhân sự công ty của bạn.'
                  : 'Tổng quan hoạt động phòng ban hôm nay.'}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-medium ring-1 ring-white/20">
                  <Shield className="w-3 h-3" /> {roleBadge}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs ring-1 ring-white/10">
                  {isAdmin ? 'Toàn hệ thống' : isDirector ? 'Toàn công ty' : 'Phòng ban'}
                </span>
                {stats.total_employees > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs ring-1 ring-white/10">
                    <Users className="w-3 h-3" /> {stats.total_employees} nhân viên
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-stretch gap-3 self-stretch lg:self-auto">
              <div className="hidden sm:flex flex-col justify-center px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-md ring-1 ring-white/15 min-w-[140px]">
                <div className="text-[10px] uppercase tracking-wider text-white/60">Giờ hệ thống</div>
                <div className="text-2xl sm:text-3xl font-mono font-bold tabular-nums">{timeStr}</div>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-white/70">
                  <span className="flex items-center gap-1"><Activity className="w-3 h-3" />Peak: {peakHour}</span>
                  {stats.anomalies > 0 && (
                    <span className="flex items-center gap-1 text-amber-200"><Zap className="w-3 h-3" />{stats.anomalies}</span>
                  )}
                </div>
              </div>
              <button
                onClick={loadDashboardData}
                disabled={refreshing}
                className="px-4 rounded-2xl bg-white/15 hover:bg-white/25 backdrop-blur-md ring-1 ring-white/20 transition disabled:opacity-50 flex items-center justify-center"
                title="Làm mới"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Mini glance bar */}
          <div className="relative mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <div className="px-3 py-2 rounded-xl bg-white/10 backdrop-blur-sm ring-1 ring-white/10">
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Có mặt</div>
              <div className="text-lg font-bold tabular-nums">
                {stats.present_today + stats.late_today}
                <span className="text-xs font-normal text-white/60">/{stats.total_employees || 0}</span>
              </div>
            </div>
            <div className="px-3 py-2 rounded-xl bg-white/10 backdrop-blur-sm ring-1 ring-white/10">
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Tỷ lệ</div>
              <div className="text-lg font-bold tabular-nums">{Math.round(attendanceRate)}%</div>
            </div>
            <div className="px-3 py-2 rounded-xl bg-white/10 backdrop-blur-sm ring-1 ring-white/10">
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Đi muộn</div>
              <div className="text-lg font-bold tabular-nums text-amber-200">{stats.late_today}</div>
            </div>
            <div className="px-3 py-2 rounded-xl bg-white/10 backdrop-blur-sm ring-1 ring-white/10">
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Chờ duyệt</div>
              <div className="text-lg font-bold tabular-nums">
                {stats.pending_leaves}
                {otStats.pending > 0 && <span className="text-xs font-normal text-white/70"> +{otStats.pending} OT</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== CẢNH BÁO VỌNG GÁC (bản quản lý) ===== */}
      <GuardDutyAlertAdmin />

      {/* ===== NHẮC NHỞ DUYỆT ===== */}
      {/* Đặt ngay dưới HERO, TRÊN các chỉ số: việc cần TAY người này làm phải đứng trước
          những con số chỉ để đọc. Cả hai thẻ tự ẩn khi không có gì. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PendingApprovalsCard inbox={inbox} loading={inboxLoading} onReload={reloadInbox} />
        <MyPendingRequestsCard inbox={inbox} loading={inboxLoading} />
      </div>

      {/* ===== KPI ROW ===== */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {canViewEmployees && (
          <KpiCard
            icon={Users}
            label="Nhân viên phải chấm công"
            value={stats.total_employees}
            sublabel={[
              stats.exempt_employees > 0 ? `${stats.exempt_employees} người được miễn chấm công` : null,
              adminStats?.employees?.newThisMonth ? `${adminStats.employees.newThisMonth} người mới tháng này` : null,
            ].filter(Boolean).join(' · ') || undefined}
            accent="blue"
            delta={adminStats?.employees?.newThisMonth || null}
            spark={presentSpark}
            onClick={() => navigate('/employee-management')}
          />
        )}
        {canViewAttendance && (
          <>
            <KpiCard
              icon={CheckCircle}
              label="Đúng giờ hôm nay"
              value={stats.present_today}
              sublabel={`${Math.round((stats.present_today / (stats.total_employees || 1)) * 100)}% quân số phải chấm công`}
              accent="green"
              spark={trendData.map(d => d.on_time || 0)}
              onClick={() => navigate('/attendance')}
            />
            <KpiCard
              icon={Clock}
              label="Đi muộn"
              value={stats.late_today}
              sublabel={totalLateMinutes > 0 ? `${totalLateMinutes.toLocaleString()} phút (30 ngày)` : undefined}
              accent="yellow"
              spark={lateSpark}
              onClick={() => navigate('/attendance')}
            />
            <KpiCard
              icon={XCircle}
              label="Vắng mặt"
              value={stats.absent_today}
              sublabel={notCheckedIn ? `${notCheckedIn} chưa check-in` : undefined}
              accent="red"
              spark={absentSpark}
              onClick={() => navigate('/supervision')}
            />
          </>
        )}
        <KpiCard
          icon={Coffee}
          label="Đang nghỉ phép"
          value={stats.on_leave || 0}
          sublabel={(stats.early_leave || 0) > 0 ? `${stats.early_leave} về sớm` : undefined}
          accent="purple"
        />
        {canViewLeave && (
          <KpiCard
            icon={FileText}
            label="Đơn chờ duyệt"
            value={stats.pending_leaves + (otStats.pending || 0)}
            sublabel={otStats.pending > 0 ? `${stats.pending_leaves} nghỉ • ${otStats.pending} OT` : 'Đơn nghỉ phép'}
            accent="orange"
            onClick={() => navigate('/approval-process')}
          />
        )}
      </div>

      {/* ===== ADMIN STRIP ===== */}
      {isAdmin && adminStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="relative overflow-hidden rounded-2xl border border-emerald-200 dark:border-emerald-800/50 bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-emerald-950/30 dark:via-gray-800 dark:to-teal-950/30 p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5" />Lương tháng
              </div>
              <TrendingUp className="w-4 h-4 text-emerald-500/40" />
            </div>
            <p className="text-xl font-bold text-emerald-900 dark:text-emerald-300 mt-2 tracking-tight">
              {adminStats.payroll?.totalThisMonth
                ? new Intl.NumberFormat('vi-VN', { notation: 'compact', maximumFractionDigits: 1 }).format(adminStats.payroll.totalThisMonth)
                : '—'}
              <span className="text-sm font-normal ml-1">₫</span>
            </p>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-amber-200 dark:border-amber-800/50 bg-gradient-to-br from-amber-50 via-white to-orange-50 dark:from-amber-950/30 dark:via-gray-800 dark:to-orange-950/30 p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />HĐ sắp hết hạn
              </div>
              {(adminStats.contracts?.expiringIn30Days || 0) > 0 && <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
            </div>
            <p className="text-xl font-bold text-amber-900 dark:text-amber-300 mt-2 tracking-tight">
              {adminStats.contracts?.expiringIn30Days || 0}
              <span className="text-xs font-normal text-amber-600 dark:text-amber-400 ml-1">trong 30 ngày</span>
            </p>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-blue-200 dark:border-blue-800/50 bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-blue-950/30 dark:via-gray-800 dark:to-indigo-950/30 p-4">
            <div className="text-xs font-medium text-blue-700 dark:text-blue-400 flex items-center gap-1.5">
              <FileSignature className="w-3.5 h-3.5" />Hợp đồng hoạt động
            </div>
            <p className="text-xl font-bold text-blue-900 dark:text-blue-300 mt-2 tracking-tight">
              {adminStats.contracts?.active || 0}
            </p>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-violet-200 dark:border-violet-800/50 bg-gradient-to-br from-violet-50 via-white to-purple-50 dark:from-violet-950/30 dark:via-gray-800 dark:to-purple-950/30 p-4">
            <div className="text-xs font-medium text-violet-700 dark:text-violet-400 flex items-center gap-1.5">
              <CalendarDays className="w-3.5 h-3.5" />Nghỉ hôm nay
            </div>
            <p className="text-xl font-bold text-violet-900 dark:text-violet-300 mt-2 tracking-tight">
              {adminStats.leave?.onLeaveToday || 0}
              <span className="text-xs font-normal text-violet-600 dark:text-violet-400 ml-1">người</span>
            </p>
          </div>
        </div>
      )}

      {/* ===== MAIN CONTENT GRID ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Live attendance overview — donut + 7d + hourly */}
          {canViewAttendance && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 shadow-sm p-5 sm:p-6">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    Trực quan chấm công hôm nay
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">Cập nhật theo thời gian thực · {timeStr}</p>
                </div>
                <button onClick={() => navigate('/attendance')} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 flex-shrink-0">
                  Chi tiết <ArrowUpRight className="w-3 h-3" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
                {/* Donut */}
                <div className="md:col-span-4 flex flex-col items-center justify-center">
                  <div className="relative">
                    <DonutChart segments={donutSegments} size={160} strokeWidth={16} />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight tabular-nums">
                        {Math.round(attendanceRate)}<span className="text-base">%</span>
                      </span>
                      <span className="text-[10px] text-gray-500 uppercase tracking-wider">Có mặt</span>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-emerald-500 rounded-full" />Đúng giờ <b className="ml-auto tabular-nums">{stats.present_today}</b></span>
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-amber-400 rounded-full" />Muộn <b className="ml-auto tabular-nums">{stats.late_today}</b></span>
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-violet-500 rounded-full" />Nghỉ <b className="ml-auto tabular-nums">{stats.on_leave || 0}</b></span>
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-orange-500 rounded-full" />Sớm <b className="ml-auto tabular-nums">{stats.early_leave || 0}</b></span>
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-rose-500 rounded-full" />Vắng <b className="ml-auto tabular-nums">{stats.absent_today}</b></span>
                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-gray-400 rounded-full" />Chưa <b className="ml-auto tabular-nums">{notCheckedIn}</b></span>
                  </div>
                </div>

                {/* 7-day trend stacked bars */}
                <div className="md:col-span-8">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Xu hướng 7 ngày</p>
                    <div className="flex items-center gap-3 text-[10px] text-gray-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" />Đúng giờ</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-sm" />Muộn</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-rose-400 rounded-sm" />Vắng</span>
                    </div>
                  </div>
                  {trendData.length > 0 ? (
                    <div className="flex items-end gap-1.5 sm:gap-2.5" style={{ height: 110 }}>
                      {trendData.map((d, i) => {
                        const t = (d.on_time || 0) + (d.late || 0) + (d.absent || 0);
                        const mx = Math.max(...trendData.map(x => (x.on_time || 0) + (x.late || 0) + (x.absent || 0)), 1);
                        const bH = t > 0 ? Math.max(6, (t / mx) * 90) : 4;
                        const oH = t > 0 ? (d.on_time / t) * bH : 0;
                        const lH = t > 0 ? (d.late / t) * bH : 0;
                        const aH = Math.max(0, bH - oH - lH);
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1.5 group" title={`${d.label}: ${d.on_time} đúng / ${d.late} muộn / ${d.absent} vắng`}>
                            <span className="text-[10px] text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 tabular-nums">{t}</span>
                            <div className="w-full max-w-[36px] flex flex-col-reverse rounded-md overflow-hidden mx-auto ring-1 ring-gray-100 dark:ring-gray-700 group-hover:ring-indigo-300 transition" style={{ height: bH }}>
                              <div className="bg-emerald-500 transition-all" style={{ height: oH }} />
                              <div className="bg-amber-400 transition-all" style={{ height: lH }} />
                              <div className="bg-rose-400 transition-all" style={{ height: aH }} />
                            </div>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">{d.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 text-center py-8">Chưa có dữ liệu</div>
                  )}
                </div>
              </div>

              {/* Hourly distribution */}
              <div className="mt-5 pt-5 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Phân bố check-in theo giờ (06h–22h)</p>
                  <span className="text-[10px] text-gray-400">Cao điểm: <b className="text-indigo-600 dark:text-indigo-400">{peakHour}</b></span>
                </div>
                <div className="flex items-end gap-1" style={{ height: 36 }}>
                  {hourlyBuckets.map((v, i) => {
                    const mx = Math.max(...hourlyBuckets, 1);
                    const h = v > 0 ? Math.max(3, (v / mx) * 32) : 2;
                    const isPeak = v === mx && v > 0;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center" title={`${(i + 6).toString().padStart(2, '0')}h: ${v} check-in`}>
                        <div
                          className={`w-full rounded-t transition-all ${isPeak ? 'bg-indigo-500' : v > 0 ? 'bg-indigo-300 dark:bg-indigo-700' : 'bg-gray-100 dark:bg-gray-700'}`}
                          style={{ height: h }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-[9px] text-gray-400 mt-1">
                  <span>06h</span><span>10h</span><span>14h</span><span>18h</span><span>22h</span>
                </div>
              </div>
            </div>
          )}

          {/* Department breakdown — director/admin only */}
          {(isAdmin || isDirector) && deptStats.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 shadow-sm p-5 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    Hiệu suất phòng ban
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">Top phòng ban theo tỷ lệ chấm công hôm nay</p>
                </div>
                {atRiskDepts.length > 0 && (
                  <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 ring-1 ring-rose-200 dark:ring-rose-800">
                    {atRiskDepts.length} phòng ban &lt; 70%
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {deptStats.map((dept, idx) => {
                  const dT = dept.total_employees || dept.total || 1;
                  const pr = dept.present_count || dept.present || 0;
                  const lt = dept.late_count || dept.late || 0;
                  const ab = dept.absent_count || dept.absent || 0;
                  const pc = Math.min(100, Math.round(((pr + lt) / dT) * 100));
                  const dn = dept.department?.department_name || dept.department_name || 'N/A';
                  const barColor = pc >= 90 ? 'bg-emerald-500' : pc >= 70 ? 'bg-amber-400' : 'bg-rose-500';
                  return (
                    <div key={idx} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5 min-w-0">
                          <span className="text-xs text-gray-400 tabular-nums">#{idx + 1}</span>
                          <span className="truncate">{dn}</span>
                        </span>
                        <div className="flex items-center gap-2 text-[11px] flex-shrink-0">
                          <span className="text-emerald-600 font-medium tabular-nums">{pr + lt}/{dT}</span>
                          {lt > 0 && <span className="text-amber-600 tabular-nums">·{lt} muộn</span>}
                          {ab > 0 && <span className="text-rose-600 tabular-nums">·{ab} vắng</span>}
                          <span className="font-bold text-gray-700 dark:text-gray-200 tabular-nums w-10 text-right">{pc}%</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-gray-700/60 rounded-full h-2 overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${pc}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent attendance feed */}
          {canViewAttendance && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 shadow-sm p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    Hoạt động chấm công gần đây
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">{recentAttendance.length} check-in mới nhất</p>
                </div>
                <button onClick={() => navigate('/attendance')} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1">
                  Xem tất cả <ArrowUpRight className="w-3 h-3" />
                </button>
              </div>
              <div className="space-y-1">
                {recentAttendance.length > 0 ? (
                  recentAttendance.map((r) => (
                    <div key={r.attendance_id} className="flex items-center justify-between py-2.5 px-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/40 transition">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 dark:from-indigo-900/40 dark:to-violet-900/40 overflow-hidden flex-shrink-0 ring-2 ring-white dark:ring-gray-800 shadow-sm">
                          {r.check_in_photo_url ? (
                            <img src={r.check_in_photo_url} alt="" className="w-full h-full object-cover" />
                          ) : r.employee?.avatar_url ? (
                            <img src={r.employee.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-indigo-600 dark:text-indigo-300 text-sm font-semibold">
                              {r.employee?.full_name?.[0] || '?'}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{r.employee?.full_name || 'N/A'}</p>
                          <p className="text-[11px] text-gray-400 truncate">{r.employee?.department?.department_name || ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="text-right hidden sm:block">
                          <span className="text-xs font-mono text-emerald-600 tabular-nums">{r.check_in_time ? new Date(r.check_in_time).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                          <span className="text-gray-300 mx-1">→</span>
                          <span className="text-xs font-mono text-blue-600 tabular-nums">{r.check_out_time ? new Date(r.check_out_time).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                        </div>
                        <StatusBadge status={r.status} />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <UserCheck className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Chưa có dữ liệu chấm công hôm nay</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ===== RIGHT COLUMN ===== */}
        <div className="space-y-5">
          {/* Priority alerts */}
          {(stats.anomalies > 0 || stats.pending_leaves > 0 || otStats.pending > 0 || atRiskDepts.length > 0 || (isAdmin && adminStats?.contracts?.expiringIn30Days > 0)) && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 shadow-sm p-5 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-rose-500/5 rounded-full blur-2xl" />
              <h3 className="relative text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                <Bell className="w-5 h-5 text-rose-500" />
                Cảnh báo ưu tiên
                <span className="ml-auto text-[10px] font-normal text-gray-400">cần xử lý</span>
              </h3>
              <div className="relative space-y-2">
                {stats.anomalies > 0 && (
                  <button onClick={() => navigate('/supervision')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-rose-50 to-rose-50/30 dark:from-rose-900/20 dark:to-rose-900/0 border border-rose-100 dark:border-rose-900/40 hover:shadow-md hover:border-rose-200 transition text-left group">
                    <div className="w-8 h-8 rounded-lg bg-rose-500/10 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-4 h-4 text-rose-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-rose-800 dark:text-rose-300">{stats.anomalies} bất thường hôm nay</p>
                      <p className="text-[11px] text-rose-600/80 dark:text-rose-400/80">Chấm công lỗi, AI phát hiện</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-rose-400 group-hover:translate-x-0.5 transition" />
                  </button>
                )}
                {stats.pending_leaves > 0 && (
                  <button onClick={() => navigate('/approval-process')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-orange-50 to-orange-50/30 dark:from-orange-900/20 dark:to-orange-900/0 border border-orange-100 dark:border-orange-900/40 hover:shadow-md transition text-left group">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-orange-800 dark:text-orange-300">{stats.pending_leaves} đơn nghỉ chờ duyệt</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-orange-400 group-hover:translate-x-0.5 transition" />
                  </button>
                )}
                {otStats.pending > 0 && (isAdmin || isDirector) && (
                  <button onClick={() => navigate('/overtime-requests')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-indigo-50 to-indigo-50/30 dark:from-indigo-900/20 dark:to-indigo-900/0 border border-indigo-100 dark:border-indigo-900/40 hover:shadow-md transition text-left group">
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                      <Timer className="w-4 h-4 text-indigo-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-300">{otStats.pending} đơn OT chờ duyệt</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-indigo-400 group-hover:translate-x-0.5 transition" />
                  </button>
                )}
                {atRiskDepts.length > 0 && (isAdmin || isDirector) && (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-amber-50/30 dark:from-amber-900/20 dark:to-amber-900/0 border border-amber-100 dark:border-amber-900/40">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <PieChart className="w-4 h-4 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">{atRiskDepts.length} phòng ban tỷ lệ thấp</p>
                      <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 truncate">{atRiskDepts.slice(0, 2).map(d => d.department?.department_name || d.department_name).join(', ')}</p>
                    </div>
                  </div>
                )}
                {isAdmin && adminStats?.contracts?.expiringIn30Days > 0 && (
                  <button onClick={() => navigate('/hr-management')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-amber-50/30 dark:from-amber-900/20 dark:to-amber-900/0 border border-amber-100 dark:border-amber-900/40 hover:shadow-md transition text-left group">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <FileSignature className="w-4 h-4 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">{adminStats.contracts.expiringIn30Days} HĐ sắp hết hạn</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-amber-400 group-hover:translate-x-0.5 transition" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Pending leaves preview */}
          {canViewLeave && pendingLeaves.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  Đơn chờ duyệt
                </h3>
                <span className="text-xs font-mono bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full">{stats.pending_leaves}</span>
              </div>
              <div className="space-y-2">
                {pendingLeaves.map((lv, i) => (
                  <div key={lv.leave_request_id || i} className="p-3 rounded-xl bg-gradient-to-r from-orange-50 to-transparent dark:from-orange-900/20 dark:to-transparent border border-orange-100 dark:border-orange-900/40">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{lv.employee?.full_name || lv.employee_name || 'N/A'}</p>
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">{lv.leave_type?.leave_type_name || lv.leave_type_name || 'Nghỉ phép'}</p>
                      </div>
                      <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded bg-orange-200/50 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 flex-shrink-0">{lv.total_days || 1}d</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1 tabular-nums">{lv.start_date ? new Date(lv.start_date).toLocaleDateString('vi-VN') : '?'} → {lv.end_date ? new Date(lv.end_date).toLocaleDateString('vi-VN') : '?'}</p>
                  </div>
                ))}
                <button onClick={() => navigate('/approval-process')} className="w-full mt-1 text-center text-xs text-indigo-600 dark:text-indigo-400 hover:underline py-1">
                  Xem tất cả →
                </button>
              </div>
            </div>
          )}

          {/* Top violators (30d) */}
          {(canViewReports || isManager) && topViolators.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <UserMinus className="w-5 h-5 text-rose-500" />
                  Top vi phạm
                </h3>
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">30 ngày</span>
              </div>
              <div className="space-y-1.5">
                {topViolators.map((v, i) => {
                  const medals = ['from-amber-400 to-orange-500', 'from-gray-300 to-gray-400', 'from-amber-700 to-amber-800'];
                  const badge = medals[i] || 'from-rose-100 to-rose-200 dark:from-rose-900/40 dark:to-rose-900/40';
                  return (
                    <div key={v.employee_id || i} className="flex items-center justify-between gap-2 py-2 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 transition">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className={`w-7 h-7 rounded-full bg-gradient-to-br ${badge} flex items-center justify-center text-xs font-bold ${i < 3 ? 'text-white' : 'text-rose-700 dark:text-rose-300'} ring-2 ring-white dark:ring-gray-800 shadow-sm flex-shrink-0`}>
                          {i + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{v.employee?.full_name || 'N/A'}</p>
                          <p className="text-[10px] text-gray-400 truncate">{v.department?.department_name || ''}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-rose-600 dark:text-rose-400 tabular-nums">{v.total_violations}<span className="text-[10px] font-normal ml-0.5">lần</span></p>
                        <p className="text-[10px] text-gray-400 tabular-nums">{v.total_late_minutes}p</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick actions */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 shadow-sm p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Truy cập nhanh
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {canViewAttendance && (
                <button onClick={() => navigate('/attendance')} className="group p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 transition text-center">
                  <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 mx-auto mb-1 group-hover:scale-110 transition" />
                  <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Chấm công</p>
                </button>
              )}
              {canViewEmployees && (
                <button onClick={() => navigate('/employee-management')} className="group p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-300 dark:hover:border-emerald-700 transition text-center">
                  <Users className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mx-auto mb-1 group-hover:scale-110 transition" />
                  <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Nhân viên</p>
                </button>
              )}
              {canViewSupervision && (
                <button onClick={() => navigate('/supervision')} className="group p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-300 dark:hover:border-indigo-700 transition text-center">
                  <Eye className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mx-auto mb-1 group-hover:scale-110 transition" />
                  <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Giám sát</p>
                </button>
              )}
              {canViewReports && (
                <button onClick={() => navigate('/reports')} className="group p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-300 dark:hover:border-purple-700 transition text-center">
                  <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400 mx-auto mb-1 group-hover:scale-110 transition" />
                  <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Báo cáo</p>
                </button>
              )}
              {canViewLeave && (
                <button onClick={() => navigate('/approval-process')} className="group p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-300 dark:hover:border-orange-700 transition text-center">
                  <ClipboardList className="w-5 h-5 text-orange-600 dark:text-orange-400 mx-auto mb-1 group-hover:scale-110 transition" />
                  <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Phê duyệt</p>
                </button>
              )}
              {isAdmin && (
                <button onClick={() => navigate('/ai-auto-check')} className="group p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:border-teal-300 dark:hover:border-teal-700 transition text-center">
                  <Camera className="w-5 h-5 text-teal-600 dark:text-teal-400 mx-auto mb-1 group-hover:scale-110 transition" />
                  <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300">AI Camera</p>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
